import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { CORE_STATE_NAME, CoreState } from "../../core/redux/redux.module";
import { of as observableOf, interval, Observable, Subject } from "rxjs";
import { delayWhen, distinctUntilChanged, map, take, takeUntil, withLatestFrom } from "rxjs/operators";

/**
 * @whatItDoes Displays an animated loading mask.
 *
 * @howToUse
 *
 * ```
 * <loading-mask [active]="true"></loading-mask>
 * ```
 *
 * ### Input Properties
 *
 * * **active**: If true, the loading mask appears.
 * * **visibilityChangeDelay**: Setting this parameter means a 'grace period' (in ms) for showing or hiding the loadmask
 *
 * @description
 *
 * This component displays a loadingmask to provide visual feedback for background processing.
 *
 * @selector "loading-mask"
 * @ngModule LoadingMaskModule
 */

/**
 * default value for change of visibility of the component (@see visibilityChangeDelay input)
 */
export const DEFAULT_VISIBILITY_CHANGE_DELAY = 200;

@Component({
    // tslint:disable-next-line:component-selector
    selector: "loading-mask",
    template: ` <div class="mkb-loadmask-wrapper" *ngIf="isActiveInner">
                    <mat-progress-spinner mode="indeterminate"></mat-progress-spinner>
                </div>`
})
export class LoadingMaskComponent implements OnInit, OnDestroy {
    @Input() visibilityChangeDelay?: number;

    @Input()
    public set active(value: boolean) {
        this._isActive = value;

        if (this._visibilityChangeDelay) {
            setTimeout(() => {
                if (this._isActive === value) {
                    this._isActiveInner = value;
                }
            }, this._visibilityChangeDelay);
        } else {
            this._isActiveInner = value;
        }
    }

    public get isActiveInner(): boolean {
        return this._isActiveInner;
    }

    protected _isActive = false;
    protected _isActiveInner = false;
    protected _isLoaderStopping = false;
    protected _visibilityChangeDelay: number;
    protected _ngrxStore: Store<any>;
    protected _coreState: Observable<CoreState>;
    protected destroy$: Subject<boolean> = new Subject();

    constructor (ngrxStore: Store<any>,
                 private changeDetectorRef: ChangeDetectorRef) {
        this._ngrxStore = ngrxStore;
        this._coreState = this._ngrxStore.select(CORE_STATE_NAME);
        this._visibilityChangeDelay = this.visibilityChangeDelay || DEFAULT_VISIBILITY_CHANGE_DELAY;
    }

    ngOnInit() {
      const mappedState = this._coreState.pipe(
        map((state) => state && (state.navigationInProgress || state.communicationProgressCount > 0)),
        distinctUntilChanged(),
      );
      mappedState.pipe(
        delayWhen((active) => active ? observableOf(1) : interval(this._visibilityChangeDelay).pipe(take(1))),
        withLatestFrom(mappedState, (actual, delayed) => {
          return actual || delayed;
        }),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      ).subscribe(
        (active) => {
          // seems like setTimeout isn't needed - kept for safety
          // ExpressionChangedAfterItHasBeenCheckedError could occur if emits follow eachother too quickly in dev environment,
          // for ex. having to show overlay at 1ms after DEFAULT_VISIBILITY_CHANGE_DELAY passed since last hide emission
          setTimeout(() => {
            this._isActiveInner = active;
            this.changeDetectorRef.markForCheck();
          });
        }
      );
    }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
